import { styled } from '@material-ui/core';

import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewContent,
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';

import { CommentZoneMui as CommentZone } from '@spotify/plugin-confidence-comments-react';

import { useBlockZoneId } from '../hooks';
import {
  SemanticColor,
  blockIdAttribute,
  generateBlockId,
  useColorResolver,
} from '../utils';
import {
  BlockTypes,
  allBlocks,
  buildContentSchema,
  excludeBlocks,
} from './blockTypes';
import { tiptapBlockMarginBottom, tiptapBlockMarginTop } from './editorStyles';

const blockContent = buildContentSchema(
  excludeBlocks(allBlocks, [BlockTypes.CALLOUT_BLOCK]),
);

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    calloutBlock: {
      setCalloutBlock: () => ReturnType;
      toggleCalloutBlock: () => ReturnType;
    };
  }
}

export type BlockAttributes = {
  backgroundColor: SemanticColor | null;
  borderColor: SemanticColor | null;
};

const StyledBlock = styled('div')({
  width: '100%',
  borderRadius: 'var(--encore-corner-radius-larger-2)',
  padding: 'var(--encore-spacing-looser)',
  marginBottom: tiptapBlockMarginBottom,
  marginTop: tiptapBlockMarginTop,
  '&:hover': {
    borderColor: 'var(--essential-subdued)',
  },
  '&:focus, &:focus-within, &:active': {
    borderColor: 'var(--essential-base)',
  },
});

const BlockComponent = ({ node, updateAttributes }: NodeViewProps) => {
  const { backgroundColor, borderColor, blockId } =
    node.attrs as BlockAttributes & { blockId: string | null };
  const { resolveBackgroundColor, resolveTextColor } = useColorResolver();
  const zoneId = useBlockZoneId('callout-block', blockId, updateAttributes);

  const resolvedBgColor = resolveBackgroundColor(backgroundColor);
  const resolvedBorderColor = resolveTextColor(borderColor);

  const getBorder = () => {
    if (resolvedBorderColor) {
      return `1px solid ${resolvedBorderColor}`;
    }
    if (!resolvedBgColor) {
      return '1px solid var(--decorative-subdued)';
    }
    return undefined;
  };

  const style: React.CSSProperties = {
    backgroundColor: resolvedBgColor ?? 'var(--background-base)',
    border: getBorder(),
  };

  const content = (
    <StyledBlock style={style}>
      <NodeViewContent />
    </StyledBlock>
  );

  return (
    <NodeViewWrapper>
      {zoneId ? (
        <CommentZone id={zoneId} component="div">
          {content}
        </CommentZone>
      ) : (
        content
      )}
    </NodeViewWrapper>
  );
};

export const Block = Node.create({
  name: 'calloutBlock',
  group: 'block',
  content: blockContent,
  defining: true,

  addAttributes() {
    return {
      backgroundColor: {
        default: null,
        parseHTML: element => element.getAttribute('data-background-color'),
        renderHTML: attributes => {
          if (!attributes.backgroundColor) {
            return {};
          }
          return { 'data-background-color': attributes.backgroundColor };
        },
      },
      borderColor: {
        default: null,
        parseHTML: element => element.getAttribute('data-border-color'),
        renderHTML: attributes => {
          if (!attributes.borderColor) {
            return {};
          }
          return { 'data-border-color': attributes.borderColor };
        },
      },
      blockId: blockIdAttribute,
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="callout-block"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'callout-block' }),
      0,
    ];
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(BlockComponent);
  },

  addCommands() {
    return {
      setCalloutBlock:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { blockId: generateBlockId() },
            content: [
              {
                type: 'paragraph',
              },
            ],
          });
        },
      toggleCalloutBlock:
        () =>
        ({ commands }) => {
          return commands.toggleWrap(this.name, { blockId: generateBlockId() });
        },
    };
  },
});
